import './BulkUpdateTool.css';
// ADDED: useMemo
import React, { useState, useEffect, useCallback, useMemo } from 'react'; 
import manualUpdateAPI from '../../services/manualUpdateAPI';
import containerAPI from '../../services/containerAPI';
import { handleApiError, formatDateTime } from '../../utils/helpers';
import { mapStatusToCategory } from '../../utils/statusCategoryMapper';
import LoadingSpinner from '../common/LoadingSpinner';
import { decodeJWT } from '../../utils/jwt';

const BulkUpdateTool = ({ onUpdate, statusMappings }) => {
  const [locationError, setLocationError] = useState('');
  const initialBulkData = {
    carrierName: '',
    carrierStatusCode: '',
    // Ensure eventTime defaults to a full ISO date for backend compatibility if necessary
    eventTime: new Date().toISOString().slice(0, 10), 
    location: ''
  };
 
  const [selectedContainers, setSelectedContainers] = useState([]);
  const [containers, setContainers] = useState([]);
  const [bulkData, setBulkData] = useState(initialBulkData); 

  // Generic handler for all bulkData form fields
  const handleBulkDataChange = (e) => {
    const { name, value } = e.target;
    setBulkData(prev => ({
      ...prev,
      [name]: value
    }));
    // Validate location against portOptions
    if (name === 'location') {
      const validPort = portOptions.some(port => port.portCode === value);
      setLocationError(validPort || value === '' ? '' : 'Invalid port code. Please select a port from the list.');
    }
  };
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [result, setResult] = useState(null);
  const [containerStatusOptions, setContainerStatusOptions] = useState([]);
  const [portOptions, setPortOptions] = useState([]);

  
  
  const filteredContainers = useMemo(() => {
    // Only show containers with both port_of_loading and port_of_discharge
    const hasPorts = c => c.portOfLoading && c.portOfDischarge;
    let filtered = [];
    if (!searchTerm.trim()) {
      filtered = containers.filter(hasPorts);
    } else {
      // Support comma-separated search for container/booking numbers
      const terms = searchTerm.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
      if (terms.length > 1) {
        filtered = containers.filter(container =>
          hasPorts(container) && terms.some(term =>
            container.containerNumber.toLowerCase().includes(term) ||
            container.bookingNumber.toLowerCase().includes(term)
          )
        );
      } else {
        const lowerSearch = terms[0];
        filtered = containers.filter(container =>
          hasPorts(container) && (
            container.containerNumber.toLowerCase().includes(lowerSearch) ||
            container.bookingNumber.toLowerCase().includes(lowerSearch) ||
            (container.portOfLoading && container.portOfLoading.toLowerCase().includes(lowerSearch)) ||
            (container.portOfDischarge && container.portOfDischarge.toLowerCase().includes(lowerSearch)) ||
            (container.vesselName && container.vesselName.toLowerCase().includes(lowerSearch))
          )
        );
      }
    }
    // Sort by containerNumber (alphanumeric)
    return filtered.slice().sort((a, b) => {
      if (!a.containerNumber) return 1;
      if (!b.containerNumber) return -1;
      return a.containerNumber.localeCompare(b.containerNumber, undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [containers, searchTerm]);
  // --- END OPTIMIZATION ---

  
  // --- START CALLBACK FIXES: Added setter dependencies ---
  // Remove useCallback, just use regular async functions
  const fetchContainerStatusOptions = async () => {
    try {
      const response = await containerAPI.getStatusOptions();
      setContainerStatusOptions(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setContainerStatusOptions([]);
    }
  };

  const fetchPorts = async () => {
    try {
      const response = await containerAPI.getPorts();
      setPortOptions(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setPortOptions([]);
    }
  };

  const loadContainers = async () => {
    try {
      const token = sessionStorage.getItem('agentToken');
      const agent = decodeJWT(token);
      const companyId = agent && agent.companyId ? agent.companyId : 1;
      const response = await containerAPI.getAll(companyId);
      setContainers(response.data);
    } catch (error) {
      console.error('Error loading containers:', error);
    }
  };
  
  useEffect(() => {
    loadContainers(); 
    fetchContainerStatusOptions(); 
    fetchPorts();      
  }, []);  
     
  const handleBulkUpdate = async () => {
    if (selectedContainers.length === 0) {
      alert('Please select at least one container');
      return;
    }

    if (!bulkData.carrierName || !bulkData.carrierStatusCode || !bulkData.location) {
      alert('Please fill in all required fields');
      return;
    }
    // Validate location before submit
    const validPort = portOptions.some(port => port.portCode === bulkData.location);
    if (!validPort) {
      setLocationError('Invalid port code. Please select a port from the list.');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // Send portCode for location
      const payload = {
        containerNumbers: selectedContainers,
        carrierName: bulkData.carrierName,
        carrierStatusCode: bulkData.carrierStatusCode,
        eventTime: bulkData.eventTime, // yyyy-MM-dd string
        location: bulkData.location
      };
      const response = await manualUpdateAPI.bulkUpdateStatus(payload);

      setResult(response.data.result);
      // NOTE: loadContainers is a dependency of this callback, so it's stable.
      await loadContainers(); // Refresh grid after update
      if (onUpdate) onUpdate();
      // Reset form state after all async operations
      setBulkData(initialBulkData);
      setSelectedContainers([]);
    } catch (error) {
      setResult({
        successCount: 0,
        failureCount: selectedContainers.length,
        failedContainers: selectedContainers,
        failureReasons: [handleApiError(error)]
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (e) => {
    // Logic is correct, uses the optimized filteredContainers
    if (e.target.checked) {
      setSelectedContainers(filteredContainers.map(c => c.containerNumber));
    } else {
      setSelectedContainers([]);
    }
  };

  const handleSelectContainer = (containerNumber, checked) => {
    if (checked) {
      setSelectedContainers(prev => [...prev, containerNumber]);
    } else {
      setSelectedContainers(prev => prev.filter(cn => cn !== containerNumber));
    }
  };

  // Use statusMappings directly
  const currentMappings = statusMappings[bulkData.carrierName] || [];

  // The JSX remains largely the same, utilizing the useMemo output: filteredContainers
  return (
    <div className="bulk-update-tool">
      <div className="tool-header">
        <h3>Bulk Status Update</h3>
        <p>Update multiple containers with the same status simultaneously</p>
      </div>

      <div className="bulk-layout">
        {/* Left Panel - Bulk Update Form */}
        <div className="bulk-form-panel">
          <div className="form-card">
            <h4>Bulk Update Settings</h4>
            
            <div className="form-group">
              <label>Carrier Source *</label>
              <input
                type="text"
                name="carrierName"
                value={bulkData.carrierName}
                onChange={handleBulkDataChange}
                disabled={loading}
                placeholder="Type carrier name"
              />
            </div>

            <div className="form-group">
              <label>Carrier Status *</label>
              <select
                name="carrierStatusCode"
                value={bulkData.carrierStatusCode}
                onChange={handleBulkDataChange}
                disabled={loading}
              >
                <option value="">Select Status</option>
                {containerStatusOptions.map((option, idx) => (
                  <option key={option.statusId ?? option.id ?? idx} value={option.carrierStatusCode ?? option.statusDesc}>
                    {option.carrierStatusText ?? option.statusDesc}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Event Date *</label>
              <input
                type="date"
                name="eventTime"
                value={bulkData.eventTime}
                onChange={handleBulkDataChange}
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label>Location *</label>
              <input
                type="text"
                name="location"
                value={bulkData.location}
                onChange={handleBulkDataChange}
                required
                list="port-list"
                placeholder="Type or select port..."
              />
              <datalist id="port-list">
                {portOptions.map(port => (
                  <option key={port.portId} value={port.portCode}>
                    {port.portName} ({port.portCode})
                  </option>
                ))}
              </datalist>
              {locationError && <span style={{ color: 'red', fontSize: '0.9em' }}>{locationError}</span>}
            </div>
 
            <div className="selection-summary">
              <strong>{selectedContainers.length}</strong> containers selected
            </div>

            <button
              onClick={handleBulkUpdate}
              disabled={loading || selectedContainers.length === 0}
              className="btn btn-primary bulk-update-btn"
            >
              {loading ? <LoadingSpinner size="small" text="" /> : 'Update Selected Containers'}
            </button>
          </div>

          {/* Results Display */}
          {result && (
            <div className="results-card">
              <h4>Update Results</h4>
              <div className="result-stats">
                <div className="stat success">
                  <span className="count">{result.successCount}</span>
                  <span className="label">Successful</span>
                </div>
                <div className="stat failure">
                  <span className="count">{result.failureCount}</span>
                  <span className="label">Failed</span>
                </div>
              </div>

              {result.failureCount > 0 && (
                <div className="failures-list">
                  <h5>Failed Updates:</h5>
                  {result.failedContainers.map((container, index) => (
                    <div key={container} className="failure-item">
                      <span className="container-number">{container}</span>
                      <span className="reason">{result.failureReasons[index]}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Panel - Container Selection */}
        <div className="container-selection-panel">
          <div className="selection-header">
            <h4>Select Containers</h4>
            <textarea
                placeholder="Search containers or bookings... (comma separated)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                rows={4}
                style={{ resize: 'vertical', width: '100%', minHeight: '38px', fontSize: '0.9rem', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
              />
          </div>

          <div className="containers-list">
            <div className="list-header">
              <label className="select-all">
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={selectedContainers.length === filteredContainers.length && filteredContainers.length > 0}
                />
                Select All
              </label>
              <span className="count">{filteredContainers.length} containers</span>
            </div>

            <div className="table-container" style={{ maxHeight: '505px', overflowY: 'auto', overflowX: 'auto' }}>
              <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead className="sticky-header">
                  <tr>
                    <th></th>
                    <th>Container</th>
                    <th>Booking</th>
                    <th>Status</th>
                    <th>Vessel / Voyage</th>
                    <th>Route</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(filteredContainers) ? filteredContainers : []).map(container => (
                    <tr key={container.id} className="container-row">
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedContainers.includes(container.containerNumber)}
                          onChange={(e) => handleSelectContainer(container.containerNumber, e.target.checked)}
                        />
                      </td>
                      <td>
                        <div className="container-number">{container.containerNumber}</div>
                      </td>
                      <td>
                        <div className="booking-number">{container.bookingNumber}</div>
                      </td>
                      <td>
                        <span className={`status-badge ${container.status}`}>{mapStatusToCategory(container.status)}</span>
                      </td>
                      <td>
                        <span className="vessel">
                          {container.vesselName || 'N/A'}
                          {container.voyageNumber && <><br /><span style={{ display: 'inline-block', fontSize: '1em', color: '#888', lineHeight: '1' }}>{container.voyageNumber}</span></>}
                        </span>
                      </td>
                      <td>
                        <span className="route">
                          {container.portOfLoading}
                          {container.portOfLoading && (
                            <>
                              <br />
                              <span style={{ display: 'inline-block', fontSize: '1.2em', color: '#888', lineHeight: '1' }}>&#8595;</span>
                            </>
                          )}
                          <br />
                          {container.portOfDischarge}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredContainers.length === 0 && (
              <div className="empty-state">
                <p>No containers found</p>
              </div>
            )}
          </div>
        </div>
      </div>

  <style>{`
        .sticky-header th {
          position: sticky;
          top: 0;
          background: #f8f9fa;
          z-index: 2;
        }
        .bulk-update-tool {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          padding: 2rem;
        }
        
        .tool-header {
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #eee;
        }
        
        .tool-header h3 {
          margin: 0 0 0.5rem 0;
          color: #333;
        }
        
        .tool-header p {
          margin: 0;
          color: #666;
        }
        
        .bulk-layout {
          display: grid;
          grid-template-columns: 350px 1fr;
          gap: 2rem;
          min-height: 600px;
        }
        
        .bulk-form-panel {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        
        .form-card, .results-card {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 1.5rem;
          border: 1px solid #e9ecef;
        }
        
        .form-card h4, .results-card h4 {
          margin: 0 0 1rem 0;
          color: #333;
        }
        
        .selection-summary {
          text-align: center;
          padding: 1rem;
          background: white;
          border-radius: 4px;
          margin: 1rem 0;
          font-size: 1.1rem;
        }
        
        .bulk-update-btn {
          width: 100%;
          padding: 1rem;
        }
        
        .result-stats {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        
        .stat {
          flex: 1;
          text-align: center;
          padding: 1rem;
          border-radius: 6px;
          color: white;
        }
        
        .stat.success {
          background: #28a745;
        }
        
        .stat.failure {
          background: #dc3545;
        }
        
        .stat .count {
          display: block;
          font-size: 1.5rem;
          font-weight: bold;
        }
        
        .stat .label {
          font-size: 0.9rem;
        }
        
        .failures-list {
          margin-top: 1rem;
        }
        
        .failures-list h5 {
          margin: 0 0 0.5rem 0;
          color: #dc3545;
        }
        
        .failure-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.5rem;
          background: white;
          border-radius: 4px;
          margin-bottom: 0.5rem;
        }
        
        .failure-item .container-number {
          font-weight: 600;
        }
        
        .failure-item .reason {
          color: #666;
          font-size: 0.9rem;
        }
        
        .container-selection-panel {
          display: flex;
          flex-direction: column;
        }
        
        .selection-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }
        
        .selection-header h4 {
          margin: 0;
          color: #333;
        }
        
        .search-box input {
          padding: 0.5rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          width: 250px;
        }
        
        .containers-list {
          flex: 1;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          overflow: hidden;
        }
        
        .list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.10rem;
          background: #f8f9fa;
          border-bottom: 1px solid #e9ecef;
        }
        
        .select-all {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 500;
        }
        
        .count {
          color: #666;
          font-size: 0.9rem;
        }
        
        .containers-grid {
          max-height: 500px;
          overflow-y: auto;
          padding: 1rem;
        }
        
        .container-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          border: 1px solid #e9ecef;
          border-radius: 6px;
          margin-bottom: 0.5rem;
          transition: all 0.3s;
        }
        
        .container-card:hover {
          border-color: #007bff;
          background: #f8f9fa;
        }
        
        .container-select input {
          margin: 0;
        }
        
        .container-info {
          flex: 1;
        }
        
        .container-number {
          font-weight: 600;
          color: #333;
          margin-bottom: 0.25rem;
        }
        
        .booking-number {
          color: #666;
          font-size: 0.9rem;
          margin-bottom: 0.5rem;
        }
        
        .container-meta {
          display: flex;
          gap: 1rem;
          align-items: center;
        }
        
        .vessel {
          color: #666;
          font-size: 0.8rem;
        }
        
        .empty-state {
          text-align: center;
          padding: 3rem;
          color: #666;
        }
        
        .table-container {
          max-height: 500px;
          min-height: 300px;
          overflow-y: auto;
          overflow-x: auto;
          width: 100%;
          display: block;
        }
        
        .table {
          width: 100%;
          border-collapse: collapse;
        }
        
        @media (max-width: 1024px) {
          .bulk-layout {
            grid-template-columns: 1fr;
          }
          .table-container {
            max-height: 300px;
          }
        }
      `}</style>
    </div>
  );
};

export default BulkUpdateTool;